import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, ShoppingCart } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { useUserByUsername, useProducts, useAddToCart, useTrackEvent } from '../hooks/useQueries';
import { useCart } from '../hooks/useCart';

export function ShopPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  
  const { data: user, isLoading: userLoading } = useUserByUsername(username || '');
  const { data: products = [], isLoading: productsLoading } = useProducts(user?.id || '');
  const addToCartMutation = useAddToCart();
  const trackEventMutation = useTrackEvent();
  const { getTotalItems } = useCart();
  
  const loading = userLoading || productsLoading;
  
  // Filter products based on search query
  const filteredProducts = searchQuery.trim() === '' 
    ? products 
    : products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleAddToCart = async (productId: string) => {
    if (!user) return;
    
    setAddingToCart(productId);
    try {
      await addToCartMutation.mutateAsync(productId);
      // The cart counter will update automatically via React Query
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    } finally {
      setAddingToCart(null);
    }
  };
  
  const handleProductClick = (productId: string) => {
    if (user) {
      trackEventMutation.mutate({
        userId: user.id,
        type: 'product_view',
        targetId: productId
      });
    }
  };
  
  const handleProductAction = (productId: string) => {
    if (user) {
      trackEventMutation.mutate({
        userId: user.id,
        type: 'product_click',
        targetId: productId
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading shop...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Shop not found</h2>
          <p className="text-gray-600">Unable to load shop data.</p>
          <button
            onClick={() => navigate(`/u/${username}`)}
            className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition duration-300"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/u/${username}`)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Profile
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {user.displayName}'s Shop
            </h1>
            <button
              onClick={() => navigate('/cart')}
              className="flex items-center text-gray-600 hover:text-gray-900 relative"
            >
              <ShoppingCart className="w-5 h-5" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getTotalItems()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
            {filteredProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={product.isAffiliate ? undefined : handleAddToCart}
                onProductClick={(productId) => {
                  handleProductClick(productId);
                  handleProductAction(productId);
                }}
                showCartButton={true}
                isLoading={addingToCart === product.id}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No products found' : 'No products yet'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? `No results for "${searchQuery}". Try a different search term.`
                : 'This shop is getting ready. Check back soon!'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}